import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import DrawerFooter from './DrawerFooter';
import DrawerHeader from './DrawerHeader';

// Drawer component for displaying content in a slide-in panel
// Props:
// - open: boolean - whether the drawer is open
// - onClose: function - callback when the drawer is closed
// - title: string - title of the drawer
// - subtitle: string - subtitle of the drawer
// - actionButtons: array - array of action buttons
// - children: node - content of the drawer
// - width: - number (use default for consistency unless really necessary)

export const Drawer = ({
	open,
	onClose,
	title,
	subtitle = '',
	actionButtons = [],
	children,
	width = 520,
}) => {
	return (
		<>
			{/*  Backdrop */}
			<Box
				data-testid='drawer-backdrop'
				onClick={onClose}
				sx={{
					position: 'absolute',
					inset: 0,
					backgroundColor: 'rgba(0,0,0,0.25)',
					opacity: open ? 1 : 0,
					pointerEvents: open ? 'auto' : 'none',
					transition: 'opacity 225ms cubic-bezier(0.4, 0, 0.2, 1)',
					zIndex: 1200,
				}}
			/>

			{/* Panel */}
			<Box
				sx={{
					position: 'absolute',
					top: 0,
					right: 0,
					bottom: 0,
					width: width,
					backgroundColor: 'background.paper',
					transform: open ? 'translateX(0)' : 'translateX(100%)',
					transition: 'transform 225ms cubic-bezier(0, 0, 0.2, 1)',
					zIndex: 1201,
					display: 'flex',
					flexDirection: 'column',
					boxShadow: open ? '-4px 0 20px rgba(0,0,0,0.08)' : 'none',
					overflowX: 'auto',
				}}
			>
				{/* Header */}
				<DrawerHeader title={title} subtitle={subtitle} onClose={onClose} />

				{/* Main Content */}
				{/* Content area with scrollable body */}
				<Box
					sx={{
						flex: '1 1 auto',
						minHeight: 0,
						overflowY: 'auto',
					}}
				>
					{children}
				</Box>

				{/* Footer (sticky) */}
				{actionButtons?.length > 0 && (
					<DrawerFooter actionButtons={actionButtons} />
				)}
			</Box>
		</>
	);
};

Drawer.propTypes = {
	open: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	title: PropTypes.node,
	subtitle: PropTypes.node,
	actionButtons: PropTypes.arrayOf(PropTypes.object),
	children: PropTypes.node.isRequired,
	width: PropTypes.number
};

export default Drawer;
